import React from "react";
import { Box, Divider, IconButton, Stack, Typography } from "@mui/material";
import InstagramIcon from "@mui/icons-material/Instagram";
import FacebookIcon from "@mui/icons-material/Facebook";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import SectionFrame from "../SectionFrame";
import { EditableText } from "../InlineEditable";
import { resolveBusinessToken } from "../config";
import { resolveLandingAssetUrl } from "../../../../utils/landingAssets";

function FooterLogo({ form, centered = false, inverse = false, compact = false }) {
  const resolvedLogo = resolveLandingAssetUrl(form.theme.logoUrl);

  if (form.footer.showLogo === false || !resolvedLogo) {
    return (
      <Typography
        sx={{
          color: inverse ? "#fff" : form.theme.secondaryColor,
          fontWeight: "var(--wp-segment-title-weight)",
          fontSize: compact ? "var(--wp-title-24)" : "var(--wp-title-30)",
          textAlign: centered ? "center" : "left",
        }}
      >
        {form.business.name || "Tu negocio"}
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        width: compact ? "var(--wp-image-190)" : "var(--wp-image-230)",
        height: compact ? "var(--wp-image-62)" : "var(--wp-image-76)",
        mx: centered ? "auto" : 0,
        borderRadius: 2,
        bgcolor: inverse ? "rgba(255,255,255,.96)" : "#fff",
        border: "1px solid",
        borderColor: inverse ? "rgba(255,255,255,.38)" : "rgba(25,36,47,.15)",
        backgroundImage: `url(${resolvedLogo})`,
        backgroundSize: "contain",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
      }}
    />
  );
}

function NavLinks({ form, productsEnabled, disabled, onCommit, inverse = false, horizontal = false, centered = false }) {
  const color = inverse ? "rgba(255,255,255,.76)" : form.theme.mutedTextColor || "#5B5D73";
  const items = [
    ["footer.homeLabel", form.footer.homeLabel, "Inicio"],
    ...(productsEnabled ? [["footer.productsLabel", form.footer.productsLabel, "Productos"]] : []),
    ["footer.contactLabel", form.footer.contactLabel, "Contacto"],
  ];
  return (
    <Stack direction={horizontal ? "row" : "column"} spacing={horizontal ? 1.1 : 0.7} alignItems={centered ? "center" : "flex-start"} justifyContent={centered ? "center" : "flex-start"} flexWrap="wrap" useFlexGap>
      {items.map(([path, value, fallback]) => (
        <EditableText key={path} value={value} fallback={fallback} disabled={disabled} onSave={(next) => onCommit(path, next)} sx={{ color, fontSize: 14, fontWeight: 700 }} />
      ))}
    </Stack>
  );
}

function ContactDetails({ form, inverse = false, centered = false }) {
  const color = inverse ? "rgba(255,255,255,.74)" : form.theme.mutedTextColor || "#5B5D73";
  const primary = form.theme.primaryColor || "#F35A3A";
  const items = [
    [PhoneOutlinedIcon, form.business.phone, "Teléfono"],
    [EmailOutlinedIcon, form.business.email, "correo@negocio.com"],
    [LocationOnOutlinedIcon, form.business.address, "Dirección del negocio"],
    [AccessTimeOutlinedIcon, form.business.schedule, "Horarios de atención"],
  ];
  return (
    <Stack spacing={1} alignItems={centered ? "center" : "stretch"}>
      {items.map(([Icon, value, fallback], index) => (
        <Stack key={`${fallback}-${index}`} direction="row" spacing={1} alignItems="center" justifyContent={centered ? "center" : "flex-start"}>
          <Icon sx={{ color: inverse ? "#fff" : primary, fontSize: 19 }} />
          <Typography sx={{ color, fontSize: 14 }}>
            {String(value || "").trim() || fallback}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}

function Socials({ inverse = false, centered = false }) {
  return (
    <Stack direction="row" spacing={0.6} justifyContent={centered ? "center" : "flex-start"}>
      {[WhatsAppIcon, InstagramIcon, FacebookIcon].map((Icon, index) => (
        <IconButton key={index} size="small" sx={{ color: inverse ? "#fff" : "#19242F", bgcolor: inverse ? "rgba(255,255,255,.1)" : "rgba(25,36,47,.06)", border: "1px solid", borderColor: inverse ? "rgba(255,255,255,.12)" : "rgba(25,36,47,.08)" }}>
          <Icon sx={{ fontSize: 18 }} />
        </IconButton>
      ))}
    </Stack>
  );
}

export default function FooterSection({ form, active, device, productsEnabled, onSelect, onCommit, onUploadImage, onRemoveImage, disabled }) {
  const isMobile = device === "mobile";
  const style = form.footer.style || "style-1";
  const inverse = style !== "style-2";
  const secondary = form.theme.secondaryColor || "#19242F";
  const primary = form.theme.primaryColor || "#F35A3A";
  const businessName = form.business.name || "Tu negocio";
  const enabled = form.sections.showFooter !== false;
  const copyright = resolveBusinessToken(form.footer.copyright, businessName) || `${businessName}. Todos los derechos reservados.`;

  const brand = ({ centered = false, compact = false } = {}) => (
    <Box sx={{ textAlign: centered ? "center" : "left" }}>
      <FooterLogo form={form} centered={centered} inverse={inverse} compact={compact} />
      <Box sx={{ mt: 1.5, maxWidth: centered ? 650 : 470, mx: centered ? "auto" : 0 }}>
        <EditableText value={form.footer.tagline || form.business.shortDescription} fallback="Una frase breve que resuma la propuesta del negocio." disabled={disabled} multiline minRows={2} onSave={(value) => onCommit("footer.tagline", value)} sx={{ color: inverse ? "rgba(255,255,255,.68)" : form.theme.mutedTextColor || "#5B5D73", lineHeight: 1.7, textAlign: centered ? "center" : "left" }} />
      </Box>
    </Box>
  );

  return (
    <SectionFrame id="footer" label="Footer" active={active} enabled={enabled} onSelect={onSelect} onToggle={(next) => onCommit("sections.showFooter", next)}>
      <Box sx={{ position: "relative", overflow: "hidden", bgcolor: style === "style-2" ? form.theme.surfaceColor || "#fff" : style === "style-3" ? "#10171e" : secondary, color: inverse ? "#fff" : form.theme.textColor || "#1A1A2E" }}>
        {style === "style-2" ? (
          <Box sx={{ maxWidth: 1440, mx: "auto", px: isMobile ? 2.5 : 6, py: isMobile ? "var(--wp-space-40)" : "var(--wp-space-48)" }}>
            <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "minmax(0,1.3fr) minmax(250px,.7fr)", alignItems: "center", gap: isMobile ? 4 : 7 }}>
              {brand()}
              <Box>
                <ContactDetails form={form} />
                <Box sx={{ mt: 2 }}><Socials /></Box>
              </Box>
            </Box>
            <Divider sx={{ my: 3.5 }} />
            <Stack direction={isMobile ? "column" : "row"} alignItems="center" justifyContent="space-between" spacing={1.5}>
              <NavLinks form={form} productsEnabled={productsEnabled} disabled={disabled} onCommit={onCommit} horizontal centered={isMobile} />
              <EditableText value={form.footer.copyright} fallback={copyright} disabled={disabled} onSave={(value) => onCommit("footer.copyright", value)} sx={{ color: form.theme.mutedTextColor || "#5B5D73", fontSize: 13, textAlign: isMobile ? "center" : "left" }} />
            </Stack>
          </Box>
        ) : style === "style-3" ? (
          <>
            <Box aria-hidden sx={{ position: "absolute", inset: 0, background: `radial-gradient(circle at 100% 0%, ${primary} 0%, transparent 34%), linear-gradient(135deg, ${secondary} 0%, #10171e 72%)` }} />
            <Box sx={{ position: "relative", zIndex: 1, maxWidth: 1040, mx: "auto", px: 2.5, py: isMobile ? "var(--wp-space-48)" : "var(--wp-space-56)" }}>
              {brand({ centered: true, compact: true })}
              <Box sx={{ mt: 2.5 }}><NavLinks form={form} productsEnabled={productsEnabled} disabled={disabled} onCommit={onCommit} inverse horizontal centered /></Box>
              <Box sx={{ mt: 3 }}><ContactDetails form={form} inverse centered /></Box>
              <Box sx={{ mt: 3 }}><Socials inverse centered /></Box>
              <Divider sx={{ my: 3.5, borderColor: "rgba(255,255,255,.12)" }} />
              <EditableText value={form.footer.copyright} fallback={copyright} disabled={disabled} onSave={(value) => onCommit("footer.copyright", value)} sx={{ color: "rgba(255,255,255,.52)", fontSize: 13, textAlign: "center" }} />
            </Box>
          </>
        ) : (
          <>
            <Box aria-hidden sx={{ position: "absolute", inset: 0, opacity: 0.16, background: `radial-gradient(circle at 15% 0%, ${primary} 0%, transparent 34%), radial-gradient(circle at 90% 100%, rgba(255,255,255,.35) 0%, transparent 30%)` }} />
            <Box sx={{ position: "relative", zIndex: 1, maxWidth: 1440, mx: "auto", px: isMobile ? 2.5 : 6, pt: isMobile ? "var(--wp-space-48)" : "var(--wp-space-64)", pb: 3 }}>
              <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1.4fr .8fr 1fr", gap: isMobile ? 4 : 6 }}>
                <Box>{brand()}<Box sx={{ mt: 2.2 }}><Socials inverse /></Box></Box>
                <Box>
                  <EditableText value={form.footer.navigationTitle} fallback="Navegación" disabled={disabled} onSave={(value) => onCommit("footer.navigationTitle", value)} sx={{ color: "#fff", fontWeight: 950, fontSize: 18, mb: 1.4 }} />
                  <NavLinks form={form} productsEnabled={productsEnabled} disabled={disabled} onCommit={onCommit} inverse />
                </Box>
                <Box>
                  <EditableText value={form.footer.contactTitle} fallback="Contacto" disabled={disabled} onSave={(value) => onCommit("footer.contactTitle", value)} sx={{ color: "#fff", fontWeight: 950, fontSize: 18, mb: 1.4 }} />
                  <ContactDetails form={form} inverse />
                </Box>
              </Box>
              <Divider sx={{ my: 4, borderColor: "rgba(255,255,255,.16)" }} />
              <EditableText value={form.footer.copyright} fallback={copyright} disabled={disabled} onSave={(value) => onCommit("footer.copyright", value)} sx={{ color: "rgba(255,255,255,.52)", fontSize: 13, textAlign: "center" }} />
            </Box>
          </>
        )}
        {style === "style-2" ? <Box sx={{ height: 6, bgcolor: primary }} /> : null}
      </Box>
    </SectionFrame>
  );
}
